// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {AttrsSchema, Component, jsx} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {
  CHART_CONTAINER_MIN_HEIGHT,
  DATA_TABLE_DEFAULT_RATIO,
  DATA_TABLE_MIN_HEIGHT,
} from 'common/components/resizable-panel/constants';
import {ResizablePanelEl} from 'common/components/resizable-panel';
import {Direction, PanelName} from 'common/components/resizable-panel/types';
import {emitTrackEvent, hasSlot} from 'common/util/dom';
import {ToggleEl} from 'common/components/toggle';
import {SelectEvent, ToggleOption} from 'common/components/toggle/types';
import {Placement, PositioningStrategy} from 'common/components/tooltip/types';
import {HORIZONTAL_CONTENT_PADDING_STYLE} from 'iron/common/report/layout/banner-layout';
import {commonTrackingEventPropertyName} from 'common/report/tracking-setup';
import {ReportLayout} from 'common/report/contexts/report-layout/types';
import {keyEventSwitcher} from 'common/util/keyboard';
import {getReportName} from 'common/report/util';

import {REPORT_LAYOUT_TO_KEYBOARD_CODE} from './constants';
import {generateShortcutLabelForReportLayout} from './utils';
import css from './index.inline.styl';

const COMPONENT_NAME = `mp-resizing-layout`;

type ResizingLayoutJsxProps = {
  on?: {
    track: (ev: CustomEvent<{eventName: TrackingEvent; props: any}>) => void;
  };
  attrs?: ResizingLayoutAttrs;
};
export interface ResizingLayoutAttrs {
  // The 'table' slot is added to DOM after report is finished loading. However, this insertion of 'table' slot does
  // not result in a re-render of this component, resulting in the table not being rendered. The change in
  // 'show-data-table' attr is needed to force a re-render.
  'show-data-table'?: boolean;

  /** Set to true when the data table will be rendered in the `report-viz` slot (removes some padding) */
  'disable-content-padding'?: boolean;
}

export enum TrackingEvent {
  SwitchReportLayout = `Switch report layout`,
}

let isGlobalScrollDisabled = false;
export function mockIsGlobalScrollDisabled(val: boolean) {
  isGlobalScrollDisabled = val;
  return isGlobalScrollDisabled;
}

export class ResizingLayout extends Component<EmptyObj, ResizingLayoutAttrs, EmptyObj, EmptyObj, IronContextRegistry> {
  // used for dynamically switching between resizable layout and non-resizable layout
  private declare resizeObserver: ResizeObserver;

  static get attrsSchema(): AttrsSchema<ResizingLayoutAttrs> {
    return {
      'show-data-table': `boolean`,
      'disable-content-padding': `boolean`,
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      contexts: [`reportLayoutManager` as const, `urlManager` as const],
      template: () => this.renderTemplate(),
    };
  }

  get canRenderDataTable() {
    return this.attr(`show-data-table`) && hasSlot(this, `table`);
  }

  onSelectReportLayout(ev: SelectEvent) {
    this.updateReportLayout(ev.detail.selected as ReportLayout);
  }

  updateReportLayout(reportLayout: ReportLayout, {keyboardShortcut}: {keyboardShortcut?: string} = {}) {
    const reportLayoutManager = this.getContext(`reportLayoutManager`);
    const prevReportLayout = reportLayoutManager.getReportLayout();
    reportLayoutManager.updateReportLayout(reportLayout);
    this.track(TrackingEvent.SwitchReportLayout, {
      [commonTrackingEventPropertyName.reportLayout]: reportLayout,
      prevReportLayout,
      keyboardShortcut,
    });
  }

  getReportLayoutOptions(): ToggleOption[] {
    const commonTooltipAttrs = {
      placement: Placement.Top,
      'position-strategy': PositioningStrategy.Absolute,
    };

    return [
      {
        label: ``,
        tooltip: {
          ...commonTooltipAttrs,
          text: `Chart and Table ${generateShortcutLabelForReportLayout(ReportLayout.SplitChartTable)}`,
        },
        icon: `layout-middle`,
        value: ReportLayout.SplitChartTable,
      },
      {
        label: ``,
        tooltip: {
          ...commonTooltipAttrs,
          text: `Table ${generateShortcutLabelForReportLayout(ReportLayout.SplitTableOnly)}`,
        },
        icon: `layout-bottom`,
        value: ReportLayout.SplitTableOnly,
      },
      {
        label: ``,
        tooltip: {
          ...commonTooltipAttrs,
          text: `Chart ${generateShortcutLabelForReportLayout(ReportLayout.SplitChartOnly)}`,
        },
        tooltipLabel: `Chart`,
        icon: `layout-top`,
        value: ReportLayout.SplitChartOnly,
      },
    ];
  }

  renderTemplate() {
    const reportLayout = this.getContext(`reportLayoutManager`).getReportLayout();
    const horizontalReportAndTogglePadding = this.attr(`disable-content-padding`)
      ? `0`
      : HORIZONTAL_CONTENT_PADDING_STYLE;
    const bottomReportAndTogglePadding = this.attr(`disable-content-padding`) ? `0` : `16px`;

    return (
      <div sel='.resizing-layout'>
        <div sel='.report-controls-container' style={{padding: `0 ${HORIZONTAL_CONTENT_PADDING_STYLE}`}}>
          <slot attrs={{name: `report-controls`}} />
        </div>
        <div sel='.chart-and-table-container'>
          <div
            sel='.report-and-toggle-container'
            class={{'report-visible': reportLayout !== ReportLayout.SplitTableOnly}}
            style={{
              '--chart-container-min-height': `${CHART_CONTAINER_MIN_HEIGHT}px`,
              paddingBottom: bottomReportAndTogglePadding,
              paddingLeft: horizontalReportAndTogglePadding,
              paddingRight: horizontalReportAndTogglePadding,
            }}
          >
            {reportLayout !== ReportLayout.SplitTableOnly ? <slot attrs={{name: `report-viz`}} /> : null}
            {this.canRenderDataTable ? (
              <ToggleEl
                sel='.report-layout-toggle'
                attrs={{
                  selected: reportLayout,
                  options: this.getReportLayoutOptions(),
                }}
                on={{
                  select: (ev) => this.onSelectReportLayout(ev),
                }}
              />
            ) : null}
          </div>
          {this.canRenderDataTable
            ? (() => {
                if (reportLayout === ReportLayout.SplitChartOnly) {
                  return <div sel='.hidden-table-placeholder' />;
                } else {
                  const tableSlotEl = <slot attrs={{name: `table`}} />;
                  if (reportLayout === ReportLayout.SplitTableOnly) {
                    return tableSlotEl;
                  } else {
                    return (
                      <ResizablePanelEl
                        sel='.bottom-panel'
                        attrs={{
                          'min-size': DATA_TABLE_MIN_HEIGHT,
                          'max-size': null,
                          'max-auto-size': null,
                          'default-size': null,
                          'disable-collapsing': true,
                          'storage-key': `data-table`,
                          'default-alignment': Direction.Bottom,
                          'panel-name': PanelName.DataTable,
                          'min-adjacent-size': CHART_CONTAINER_MIN_HEIGHT,
                          'default-ratio': DATA_TABLE_DEFAULT_RATIO,
                        }}
                      >
                        {tableSlotEl}
                      </ResizablePanelEl>
                    );
                  }
                }
              })()
            : null}
        </div>
      </div>
    );
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(`keydown`, this.switchLayout);
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    document.removeEventListener(`keydown`, this.switchLayout);

    super.disconnectedCallback();
  }

  switchLayout = (ev: KeyboardEvent) => {
    return keyEventSwitcher(
      Array.from(REPORT_LAYOUT_TO_KEYBOARD_CODE.entries()).reduce((acc, [reportLayout, keyboardShortcut]) => {
        acc[keyboardShortcut] = () => {
          if (this.canRenderDataTable) {
            this.updateReportLayout(reportLayout, {keyboardShortcut});
          }
        };
        return acc;
      }, {}),
      {stopPropagation: true},
    )(ev);
  };

  private track(eventName: TrackingEvent, props = {}) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix: COMPONENT_NAME,
      props: {
        tab: getReportName(),
        ...props,
      },
    });
  }
}

export const ResizingLayoutEl = defineAndMakeJsxFactory<ResizingLayoutJsxProps>(COMPONENT_NAME, ResizingLayout);
export const ResizingLayoutReact = panel2react<ResizingLayoutJsxProps>(COMPONENT_NAME, ResizingLayout);
